import { FormControl, Validators } from '@angular/forms';
import { carrierUliOriginValidator } from './uli-item-truck-details.validators';
import { DriverIdType } from '../../../../administration/hub/constants/driver.constant';
import { TruckLoadingType } from '../../../../administration/hub/constants/hub.constant';
import { UliItemOverviewModel } from '../models/uli-item-overview.model';
import { CarrierModel } from '../../../../../../common/models';
import { TruckSize, VEHICLE_ID_PATTERN, UliOrigin } from '../../../../../../common/constants';
import { makeControlRequiredByKeyValidator, mustNotBeBlankValidator } from '../../../../../../common/utils';
import { UliItemTruck } from './uli-truck.model';

export enum TruckDetailsViewModeEnum {
  Edit = 'EDIT',
  Read = 'READ',
}

export class UliItemOverviewTruckInfoUpdateModel {
  carrierId: string | null = null;
  customsGoods = false;
  dangerousGoods = false;
  driverFirstName: string | null = null;
  driverLastName: string | null = null;
  loadingTypes: TruckLoadingType[] = [];
  driverIdNumber: string | null = null;
  driverIdType: DriverIdType | null = null;
  driverPhone: string | null = null;
  swapBody = false;
  truckId: number | null = null;
  truckPlate: string | null = null;
  trailerPlate: string | null = null;
  truckPlateCountry: string | null = null;
  trailerPlateCountry: string | null = null;
  truckSize: TruckSize = null;
  lengthOfNextBreak = 0;
  remainingDrivingTime = 0;
  truckSizeDescription = null;

  constructor(params: UliItemOverviewTruckInfoUpdateFormModel) {
    if (params) {
      this.truckId = params.truckId;
      this.truckPlate = params.truckPlateId;
      this.trailerPlate = params.trailerPlateId;
      this.truckPlateCountry = params.truckPlateIdCountryOfOrigin;
      this.trailerPlateCountry = params.trailerPlateIdCountryOfOrigin;
      this.driverIdNumber = params.permitNumber;
      this.driverIdType = params.permitType;
      this.driverPhone = params.phoneNumber;
      this.driverFirstName = params.firstName;
      this.driverLastName = params.lastName;
      this.truckSize = params.truckSize;
      this.carrierId = params.carrier?.extId;
      this.customsGoods = params.customsGoods;
      this.dangerousGoods = params.dangerousGoods;
      this.loadingTypes = params.loadingTypes;
      this.swapBody = params.swapBody;
    }
  }
}

export class UliItemOverviewTruckInfoUpdateFormModel {
  truckId: number | null = null;
  truckPlateId: string | null = null;
  trailerPlateId: string | null = null;
  truckPlateIdCountryOfOrigin: string | null = null;
  trailerPlateIdCountryOfOrigin: string | null = null;
  truckSize: TruckSize | null = null;
  firstName: string | null = null;
  lastName: string = null;
  permitNumber: string | null = null;
  permitType: DriverIdType | null = null;
  phoneNumber: string | null = null;
  carrier: CarrierModel | null = null;
  customsGoods = false;
  dangerousGoods = false;
  swapBody = false;
  loadingTypes: TruckLoadingType[] = [];

  constructor(params?: UliItemOverviewModel) {
    if (params) {
      const { truckInfos } = params;

      this.truckId = truckInfos[0].truckInformationView.truckId;
      this.truckPlateId = truckInfos[0].truckInformationView.truckPlateId;
      this.trailerPlateId = truckInfos[0].truckInformationView.trailerPlateId;
      this.truckPlateIdCountryOfOrigin = truckInfos[0].truckInformationView.truckPlateIdCountryOfOrigin;
      this.trailerPlateIdCountryOfOrigin = truckInfos[0].truckInformationView.trailerPlateIdCountryOfOrigin;
      this.truckSize = truckInfos[0].truckInformationView?.truckSize;
      this.firstName = truckInfos[0].driverInformation?.firstName;
      this.lastName = truckInfos[0].driverInformation?.lastName;
      this.permitNumber = truckInfos[0].driverInformation?.permitNumber;
      this.permitType = truckInfos[0].driverInformation?.permitType;
      this.phoneNumber = truckInfos[0].driverInformation?.phoneNumber;
      this.carrier = truckInfos[0].carrierView;
      this.customsGoods = truckInfos[0].truckInformationView?.customsGoods;
      this.dangerousGoods = truckInfos[0].truckInformationView?.dangerousGoods;
      this.swapBody = truckInfos[0].truckInformationView?.swapBody;
      this.loadingTypes = truckInfos[0].truckInformationView?.loadingTypes;
    }
  }
}

export class UliItemOverviewTruckInfoUpdateForm {
  constructor(
    model: UliItemOverviewTruckInfoUpdateFormModel,
    uliOrigin: UliOrigin,
    public truckId: FormControl<number> = new FormControl<number>(model.truckId),
    public truckPlateId: FormControl<string> = new FormControl<string>(model.truckPlateId, [
      Validators.pattern(VEHICLE_ID_PATTERN),
      mustNotBeBlankValidator,
    ]),
    public trailerPlateId: FormControl<string> = new FormControl<string>(model.trailerPlateId, [
      Validators.pattern(VEHICLE_ID_PATTERN),
      mustNotBeBlankValidator,
    ]),
    public truckPlateIdCountryOfOrigin: FormControl<string> = new FormControl<string>(
      model.truckPlateIdCountryOfOrigin,
      [],
    ),
    public trailerPlateIdCountryOfOrigin: FormControl<string> = new FormControl<string>(
      model.trailerPlateIdCountryOfOrigin,
      [],
    ),
    public truckSize: FormControl<TruckSize> = new FormControl<TruckSize>(model.truckSize),
    public firstName: FormControl<string> = new FormControl<string>(model.firstName),
    public lastName: FormControl<string> = new FormControl<string>(model.lastName),
    public permitNumber: FormControl<string> = new FormControl<string>(model.permitNumber),
    public permitType: FormControl<DriverIdType> = new FormControl<DriverIdType>(model.permitType, [
      makeControlRequiredByKeyValidator('permitNumber'),
    ]),
    public carrier: FormControl<CarrierModel> = new FormControl<CarrierModel>(model.carrier, [
      carrierUliOriginValidator(uliOrigin),
    ]),
    public phoneNumber: FormControl<string> = new FormControl<string>(model.phoneNumber),
    public customsGoods: FormControl<boolean> = new FormControl<boolean>(model.customsGoods),
    public dangerousGoods: FormControl<boolean> = new FormControl<boolean>(model.dangerousGoods),
    public swapBody: FormControl<boolean> = new FormControl<boolean>(model.swapBody),
    public loadingTypes: FormControl<TruckLoadingType[]> = new FormControl(model.loadingTypes, [Validators.required]),
  ) {}
}

export class UliItemTruckToTruckInfoConvertor extends UliItemOverviewTruckInfoUpdateFormModel {
  constructor(params?: UliItemTruck) {
    super();
    if (params) {
      const { truckInformationView, driverInformation, carrierView } = params;

      this.truckId = truckInformationView.truckId;
      this.truckPlateId = truckInformationView.truckPlateId;
      this.truckPlateIdCountryOfOrigin = truckInformationView.truckPlateIdCountryOfOrigin;
      this.trailerPlateId = truckInformationView.trailerPlateId;
      this.trailerPlateIdCountryOfOrigin = truckInformationView.trailerPlateIdCountryOfOrigin;
      this.truckSize = truckInformationView.truckSize;
      this.firstName = driverInformation.firstName;
      this.lastName = driverInformation.lastName;
      this.phoneNumber = driverInformation.phoneNumber;
      this.permitNumber = driverInformation.permitNumber;
      this.permitType = driverInformation.permitType;
      this.loadingTypes = truckInformationView.loadingTypes;
      this.swapBody = truckInformationView.swapBody;
      this.customsGoods = truckInformationView.customsGoods;
      this.dangerousGoods = truckInformationView.dangerousGoods;
      this.carrier = carrierView;
    }
  }
}
